import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Dropdown } from '@/components/cash-collections/components/Dropdown'
import { SegmentedToggle } from '@/components/sales-report/components/SegmentedToggle'
import { InfoPopover } from '@/components/InfoPopover'
import { MetricDeltas } from '@/components/sales-report/components/MetricDeltas'
import type { Currency, DSDCenterProps } from '../types'
import { consolidate, consolidateIncluded, scaleCurrency } from './consolidate'
import { MONO, fmt, monthLabel } from './format'
import { ClientFilter } from './ClientFilter'
import { UtilisationCard } from './Utilisation'
import { CreditByClientCard } from './CreditByClient'
import { VolumeMixCard } from './VolumeMix'
import { IncludedClientsCard } from './IncludedClients'
import { InvoicingCard } from './Invoicing'
import { CategorySplitCard } from './CategorySplit'
import { CategoryDrill } from './CategoryDrill'
import {
  UNCLASSIFIED,
  buildGroups,
  hasV2,
  lineRefs,
  splitAdd,
  splitSeries,
  splitZero,
  volumeAdd,
  volumeFig,
  volumeRow,
  volumeZero,
  type LineRef,
  type PcView,
  type SplitFig,
} from './classification'

type View = Currency | 'Consolidated'
type RangeKey = '1M' | '3M' | '6M' | '12M' | 'All'
const RANGES: RangeKey[] = ['1M', '3M', '6M', '12M', 'All']

/** DSD Center — Planning Center + Lab economics. What was sold (Gross), how it
 *  was funded (cash vs PC credit), and the resulting True Sale, split by
 *  Department › Category (Planning Center disciplines or stages, Lab categories,
 *  the Unclassified remainder). EUR / USD / Consolidated, month-navigable, with
 *  utilisation, invoicing, volume mix, credit by client and included clients. */
export function DSDCenter({ data }: DSDCenterProps) {
  const [view, setView] = useState<View>('Consolidated')
  const [selectedMonth, setSelectedMonth] = useState(data.months[data.months.length - 1])
  const [drillLine, setDrillLine] = useState<LineRef | null>(null)
  const [splitView, setSplitView] = useState<PcView>('Discipline')
  const [volumeView, setVolumeView] = useState<PcView>('Discipline')
  const [range, setRange] = useState<RangeKey>('All')
  const [clientFilter, setClientFilter] = useState<string[]>([])
  const v2 = hasV2(data)
  const refs = lineRefs(data)

  const views: View[] = [...data.currencies, 'Consolidated']
  const isCon = view === 'Consolidated'
  const displayCurrency: Currency = isCon ? 'EUR' : (view as Currency)
  const consolidated = useMemo(() => consolidate(data), [data])
  const cur = isCon ? consolidated : data.data[view as Currency]
  const mi = Math.max(0, data.months.indexOf(selectedMonth))
  const curRate = data.fxRates[selectedMonth]

  const card = 'rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900'

  // Inclusive-contract clients resolved to the display currency (per-client monthly).
  const includedSrc = isCon ? consolidateIncluded(data) : data.includedClients.filter((c) => c.cur === view)

  // ---- Client filter: focus the whole board on up to 5 clients. The two per-client
  // cards filter for real; the aggregate cards are estimated to the selected clients'
  // share of spend (footprint), per month — synthetic, flagged in the UI.
  const footprint = new Map<string, number[]>()
  const addFp = (email: string, arr: number[], offset = 0) => {
    const f = footprint.get(email) ?? data.months.map(() => 0)
    arr.forEach((v, i) => {
      const j = i + offset
      if (j >= 0 && j < f.length) f[j] += v
    })
    footprint.set(email, f)
  }
  includedSrc.forEach((c) => addFp(c.clinic, c.paidValue)) // gross footprint (real)
  const inclSet = new Set(includedSrc.map((c) => c.clinic))
  // Credit-only clients have no gross in the data — approximate it from PC credit
  // used via the board's gross-to-PC-credit ratio, so their footprint is comparable.
  const sumA = (a: number[]) => a.reduce((x, y) => x + y, 0)
  const grossToPc = sumA(cur.split.pcCredit.total) > 0 ? sumA(cur.split.gross.total) / sumA(cur.split.pcCredit.total) : 1
  cur.creditByClient.clinics.forEach((c) => {
    if (!inclSet.has(c.clinic)) addFp(c.clinic, c.monthly.map((v) => v * grossToPc), 5) // credit months Jun-25 … = data month 5+
  })
  const roster = [...footprint.keys()].sort()
  const filterActive = clientFilter.length > 0
  const allFp = data.months.map((_, i) => [...footprint.values()].reduce((s, f) => s + f[i], 0))
  const selFp = data.months.map((_, i) => clientFilter.reduce((s, e) => s + (footprint.get(e)?.[i] ?? 0), 0))
  const fraction = data.months.map((_, i) => (filterActive ? (allFp[i] ? selFp[i] / allFp[i] : 0) : 1))
  const fCur = filterActive ? scaleCurrency(cur, fraction) : cur

  // ---- Date-range window: N months ending at the selected month ----
  const rangeMonths = range === 'All' ? mi + 1 : range === '1M' ? 1 : range === '3M' ? 3 : range === '6M' ? 6 : 12
  const winStart = Math.max(0, mi - rangeMonths + 1)
  const winEnd = mi
  const winMonths = data.months.slice(winStart, winEnd + 1)
  const winMi = winMonths.length - 1
  const slice = <T,>(a: T[]) => a.slice(winStart, winEnd + 1)
  const sumWin = (a: number[]) => a.slice(winStart, winEnd + 1).reduce((x, y) => x + y, 0)
  const periodLabel = rangeMonths === 1 ? monthLabel(selectedMonth) : `${monthLabel(winMonths[0])} – ${monthLabel(selectedMonth)}`

  // Planning Centre split — windowed totals (client-scaled when filtering)
  const { gross, pcCredit, trueSale } = fCur.split
  const grossM = sumWin(gross.total)
  const pcM = sumWin(pcCredit.total)
  const trueM = sumWin(trueSale.total)

  const totalFig: SplitFig = { gross: grossM, pcCredit: pcM, trueSale: trueM }

  // Split by Department › Category — windowed Gross / PC credit / True Sale per line.
  const splitFig = (ref: LineRef): SplitFig => ({
    gross: sumWin(splitSeries(gross, ref, refs.lab.id)),
    pcCredit: sumWin(splitSeries(pcCredit, ref, refs.lab.id)),
    trueSale: sumWin(splitSeries(trueSale, ref, refs.lab.id)),
  })
  const splitGroups = v2 ? buildGroups(data, splitView, splitFig, splitAdd, splitZero) : null

  // Category drill-down — the clicked line, its department and (per currency,
  // single month, unfiltered) the itemised orders keyed "<month>|<categoryId>".
  const drillDept = drillLine ? (drillLine.id === UNCLASSIFIED ? refs.unclassified : drillLine.department === refs.lab.id ? refs.lab : refs.pc) : null
  const drillDeptFig = !drillLine
    ? splitZero()
    : drillLine.id === UNCLASSIFIED
      ? splitFig(drillLine)
      : (drillLine.department === refs.lab.id ? refs.lab.categories : refs.pc.disciplines).reduce((t, r) => splitAdd(t, splitFig(r)), splitZero())
  const drillVolumeRow = drillLine ? volumeRow(fCur, drillLine) : undefined
  const dOrders =
    drillLine && drillLine.kind === 'category' && !isCon && rangeMonths === 1 && !filterActive
      ? data.orders?.[view as Currency]?.[`${selectedMonth}|${drillLine.id}`]
      : undefined
  const ordersHint = isCon ? ' — itemised orders are per currency, so switch to EUR or USD' : rangeMonths !== 1 ? ' — set the range to 1M to drill a single month' : ''

  // ---- Period comparisons: vs prior equal-length period + vs same period last year ----
  const L = winEnd - winStart + 1
  const lyS = winStart - 12
  const lyE = winEnd - 12
  const sumRange = (a: number[], s: number, e: number) => (s >= 0 && e < a.length && s <= e ? a.slice(s, e + 1).reduce((x, y) => x + y, 0) : null)
  const cmpSum = (a: number[]) => ({ current: sumWin(a), prevMonth: sumRange(a, winStart - L, winStart - 1), prevYear: sumRange(a, lyS, lyE) })
  const avgRange = (pct: number[], made: number[], s: number, e: number) => {
    if (s < 0 || e >= pct.length || s > e) return null
    const idx: number[] = []
    for (let i = s; i <= e; i++) if (made[i] > 0) idx.push(i)
    return idx.length ? Math.round(idx.reduce((t, i) => t + pct[i], 0) / idx.length) : null
  }
  const prevLabel = rangeMonths === 1 ? 'prior month' : 'prior period'
  const prevYearLabel = lyS >= 0 ? (rangeMonths === 1 ? monthLabel(data.months[lyE]) : `${monthLabel(data.months[lyS])} – ${monthLabel(data.months[lyE])}`) : 'last year'
  const cmpLabels = { prevYearLabel, prevLabel }
  const trueCmp = { ...cmpSum(trueSale.total), ...cmpLabels }
  const invCmp = { ...cmpSum(fCur.invoicing.invoiced), ...cmpLabels }
  const prodMonthly = fCur.volume.monthly.totalSettled.map((v, i) => v + fCur.volume.includedValue[i])
  const volCmp = { ...cmpSum(prodMonthly), ...cmpLabels }
  const utilCmp = {
    current: avgRange(fCur.utilisation.usedPct, fCur.utilisation.madeAvailable, winStart, winEnd) ?? 0,
    prevMonth: avgRange(fCur.utilisation.usedPct, fCur.utilisation.madeAvailable, winStart - L, winStart - 1),
    prevYear: avgRange(fCur.utilisation.usedPct, fCur.utilisation.madeAvailable, lyS, lyE),
    ...cmpLabels,
  }

  // Windowed monthly charts (client-scaled when filtering)
  const winUtil = {
    madeAvailable: slice(fCur.utilisation.madeAvailable),
    used: slice(fCur.utilisation.used),
    manuallyRemoved: slice(fCur.utilisation.manuallyRemoved),
    usedPct: slice(fCur.utilisation.usedPct),
  }
  const winInvoicing = { invoiced: slice(fCur.invoicing.invoiced), afterWindow: fCur.invoicing.afterWindow, pipeline: slice(fCur.invoicing.pipeline), priorYearDelivered: fCur.invoicing.priorYearDelivered }
  const vmo = fCur.volume.monthly
  const winVolume = {
    monthly: {
      paidUnits: slice(vmo.paidUnits),
      includedUnits: slice(vmo.includedUnits),
      totalUnits: slice(vmo.totalUnits),
      cashCollected: slice(vmo.cashCollected),
      pcCredit: slice(vmo.pcCredit),
      totalSettled: slice(vmo.totalSettled),
      includedShare: slice(vmo.includedShare),
    },
    includedValue: sumWin(fCur.volume.includedValue),
    includedValueMonthly: slice(fCur.volume.includedValue),
  }
  const volumeGroups = v2 ? buildGroups(data, volumeView, (ref) => volumeFig(volumeRow(fCur, ref), sumWin), volumeAdd, volumeZero) : null

  // Inclusive-contract clients — real per-client, filtered to the selection + windowed.
  const includedList = includedSrc
    .filter((c) => !filterActive || clientFilter.includes(c.clinic))
    .map((c) => {
      const paidUnits = sumWin(c.paidUnits)
      const includedUnits = sumWin(c.includedUnits)
      return { clinic: c.clinic, group: c.group, cur: c.cur, paidUnits, includedUnits, totalUnits: paidUnits + includedUnits, paidValue: sumWin(c.paidValue) }
    })
    .filter((c) => c.totalUnits > 0)

  // PC credit by client — real per-client, filtered to the selection, windowed + re-totalled.
  const cb = cur.creditByClient
  const cEnd = winEnd - 5
  const winCredit =
    cEnd < 0
      ? { months: [] as string[], clinics: [] as typeof cb.clinics }
      : (() => {
          const ce = Math.min(cEnd, cb.months.length - 1)
          const cs = Math.min(Math.max(0, winStart - 5), ce)
          const months = cb.months.slice(cs, ce + 1)
          const clinics = cb.clinics
            .filter((cl) => !filterActive || clientFilter.includes(cl.clinic))
            .map((cl) => {
              const monthly = cl.monthly.slice(cs, ce + 1)
              return { ...cl, monthly, total: monthly.reduce((a, b) => a + b, 0) }
            })
            .filter((cl) => cl.total > 0)
            .sort((a, b) => b.total - a.total)
          return { months, clinics }
        })()

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">DSD Center</h1>
          <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
            <span className="font-medium text-stone-700 dark:text-stone-200">{periodLabel}</span> ·{' '}
            <span className="font-medium">{isCon ? 'Consolidated €' : view}</span> · Planning Center + Lab · YTD {data.ytdYear}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Dropdown
            label="Month"
            value={selectedMonth}
            options={[...data.months].reverse().map((m) => ({ value: m, label: monthLabel(m) }))}
            onChange={setSelectedMonth}
          />
          <SegmentedToggle options={RANGES} value={range} onChange={setRange} ariaLabel="Date range" />
          <SegmentedToggle options={views} value={view} onChange={setView} ariaLabel="Currency" />
        </div>
      </div>

      {/* Client filter — focuses the whole board on the selected clients */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs font-medium uppercase tracking-wide text-stone-400 dark:text-stone-500">Client</span>
        <ClientFilter clients={roster} selected={clientFilter} onChange={setClientFilter} />
        {filterActive && (
          <>
            <button
              onClick={() => setClientFilter([])}
              className="text-[11px] text-stone-400 underline-offset-2 hover:text-stone-600 hover:underline dark:text-stone-500 dark:hover:text-stone-300"
            >
              Clear
            </button>
            <span className="text-[11px] text-stone-400 dark:text-stone-500">
              {clientFilter.length} client{clientFilter.length > 1 ? 's' : ''} · PC credit &amp; included clients are exact; split, utilisation, invoicing &amp; volume are{' '}
              <span className="text-champagne-700 dark:text-champagne-300">estimated to their share</span>.
            </span>
          </>
        )}
      </div>

      {/* Section 1 — Planning Center + Lab split: True Sale = Gross − PC credit */}
      <div className="grid gap-4 lg:grid-cols-12">
        <div className={cn(card, 'p-6 lg:col-span-5')}>
          <div className="flex items-start justify-between gap-2">
            <p className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">
              Planning Center + Lab · True Sale · {periodLabel}
              <InfoPopover term="trueSale" />
            </p>
            {isCon && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-stone-100/80 px-2 py-0.5 text-[11px] text-stone-500 dark:bg-stone-800/70 dark:text-stone-400">
                <span className={MONO}>€1 = ${curRate?.toFixed(4)}</span>
                <InfoPopover term="consolidatedFx" />
              </span>
            )}
          </div>
          <p className={cn('mt-3 text-4xl font-semibold tracking-tight sm:text-5xl', MONO)}>{fmt(trueM, displayCurrency)}</p>
          <MetricDeltas current={trueM} prevMonth={trueCmp.prevMonth} prevYear={trueCmp.prevYear} prevYearLabel={prevYearLabel} prevLabel={prevLabel} currency={displayCurrency} size="lg" />
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-stone-500 dark:text-stone-400">
                Gross Sale <InfoPopover term="grossSale" />
              </span>
              <span className={MONO}>{fmt(grossM, displayCurrency)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-stone-500 dark:text-stone-400">
                − PC credit consumed <InfoPopover term="pcCreditConsumed" />
              </span>
              <span className={cn('text-red-600 dark:text-red-400', MONO)}>−{fmt(pcM, displayCurrency)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-stone-100 pt-2 dark:border-stone-800">
              <span className="font-medium">True Sale</span>
              <span className={cn('font-semibold', MONO)}>{fmt(trueM, displayCurrency)}</span>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-stone-400 dark:text-stone-500">
            {grossM ? Math.round((pcM / grossM) * 100) : 0}% of gross worked through the lab was funded by pre-paid PC credit.
          </p>
        </div>

        <CategorySplitCard
          className={cn(card, 'overflow-hidden lg:col-span-7')}
          groups={splitGroups}
          total={totalFig}
          view={splitView}
          onViewChange={setSplitView}
          onSelect={setDrillLine}
          currency={displayCurrency}
          periodLabel={periodLabel}
        />
      </div>

      {/* Section 2 + 6 — Utilisation and Planning Centre invoicing (monthly reads, windowed) */}
      <div className="grid gap-4 lg:grid-cols-2">
        <UtilisationCard util={winUtil} currency={displayCurrency} months={winMonths} comparison={utilCmp} />
        <InvoicingCard invoicing={winInvoicing} currency={displayCurrency} months={winMonths} comparison={invCmp} />
      </div>

      {/* Section 4 — Volume mix (fully windowed) */}
      <VolumeMixCard
        volume={winVolume}
        currency={displayCurrency}
        monthIdx={winMi}
        months={winMonths}
        periodLabel={periodLabel}
        comparison={volCmp}
        groups={volumeGroups}
        view={volumeView}
        onViewChange={setVolumeView}
      />

      {/* Section 3 — PC credit by client (fully windowed) */}
      <CreditByClientCard data={winCredit} currency={displayCurrency} />

      {/* Section 5 — Included-contract clients (fully windowed) */}
      <IncludedClientsCard clients={includedList} currency={displayCurrency} />

      {/* Category drill-down */}
      <CategoryDrill
        onClose={() => setDrillLine(null)}
        line={drillLine}
        departmentName={drillDept?.name ?? ''}
        periodLabel={periodLabel}
        currency={displayCurrency}
        fig={drillLine ? splitFig(drillLine) : splitZero()}
        departmentFig={drillDeptFig}
        totalFig={totalFig}
        volume={drillVolumeRow ? volumeFig(drillVolumeRow, sumWin) : null}
        orders={dOrders}
        ordersHint={ordersHint}
      />
    </div>
  )
}
